import React, { useState, useEffect } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Privacy Data                                                                */
/* ─────────────────────────────────────────────────────────────────────────── */

interface PrivacySection {
  id: string;
  title: string;
  icon: string;
  content: string[];
}

const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: 'zmdi-shield-check',
    content: [
      'We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or place an order with us.',
      'Please read this policy carefully. If you disagree with its terms, please discontinue use of our site. By using our website or purchasing from us, you consent to the practices described in this policy.',
      'We may update this Privacy Policy from time to time. The updated version will be indicated by a revised date at the top of the page. We encourage you to review this policy periodically to stay informed.',
      'As a clothing retailer committed to quality and customer satisfaction, we take the security and confidentiality of your personal and financial data with the utmost seriousness.',
    ],
  },
  {
    id: 'collection',
    title: 'Information We Collect',
    icon: 'zmdi-collection-item',
    content: [
      'When you place an order, we collect personal details including your full name, billing address, shipping address, email address, and phone number. This information is necessary to process and deliver your order.',
      'Payment information such as credit card numbers is collected at checkout but is processed entirely by our secure, PCI-DSS compliant payment providers. We do not store your card details on our servers.',
      'When you browse our website, we automatically collect certain technical information including your IP address, browser type, device type, pages visited, time spent on pages, and referring URLs. This data is collected through cookies and similar technologies.',
      'If you contact our customer support team, we retain records of your communications, including emails and any attachments, in order to resolve your enquiry and improve our service.',
      'If you sign up for our newsletter or create an account, we collect your email address and any style or size preferences you provide. You may unsubscribe from marketing communications at any time.',
      'We may collect sizing information and style preferences you voluntarily share to help personalise your shopping experience and product recommendations.',
    ],
  },
  {
    id: 'use',
    title: 'How We Use Your Information',
    icon: 'zmdi-settings',
    content: [
      'We use your personal information to process and fulfil your orders, arrange shipping and delivery, send order confirmation and tracking notifications, and handle returns, exchanges, and refunds.',
      'Your contact details are used to communicate with you about your order, respond to your enquiries, and provide customer support.',
      'With your consent, we may use your email address to send you marketing communications about new arrivals, seasonal collections, promotions, and exclusive offers. You can opt out at any time by clicking "unsubscribe" in any marketing email or by contacting us directly.',
      'We use aggregated, anonymised website analytics data to understand how visitors use our site, improve our product offerings, and enhance the overall shopping experience.',
      'We may use your purchase history and style preferences to personalise outfit recommendations and make your future shopping experience more relevant to your taste.',
      'Where required by law, we may use your information to comply with legal obligations, enforce our terms and conditions, prevent fraud, and protect the safety and security of our customers and staff.',
    ],
  },
  {
    id: 'sharing',
    title: 'Sharing Your Information',
    icon: 'zmdi-share',
    content: [
      'We do not sell, trade, or rent your personal information to third parties for their marketing purposes.',
      'We share your name and delivery address with our logistics and courier partners solely for the purpose of fulfilling and tracking your order. These partners are contractually obligated to keep your data secure and confidential.',
      'Your payment details are shared with our payment processing partners as necessary to complete your transaction. These providers operate under strict data security standards and are not permitted to use your data for any other purpose.',
      'We may share information with third-party service providers who assist us in operating our website, managing email communications, and analysing site performance. These providers have access only to the data necessary to perform their specific functions.',
      'We may disclose your information if required to do so by law, in response to a court order or government authority, or to protect our rights, property, or safety — or that of our customers and the public.',
      'In the event of a business merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and any new privacy terms that may apply.',
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking',
    icon: 'zmdi-globe',
    content: [
      'We use cookies — small text files stored on your device — to enhance your browsing experience, remember your preferences such as saved sizes and wishlists, and understand how you interact with our website.',
      'Essential cookies are required for the website to function correctly (e.g., maintaining your shopping cart and session). These cannot be disabled.',
      'Performance cookies collect anonymised data about how visitors use our site, helping us identify areas for improvement. These are used by analytics tools such as Google Analytics.',
      'Marketing cookies may be set by us or our advertising partners to track your browsing activity across websites and deliver relevant fashion advertisements tailored to your interests. You can opt out of these at any time.',
      'You can control or disable cookies through your browser settings. Please note that disabling certain cookies may affect your ability to use some features of our website, such as saved items or personalised recommendations.',
      'We do not use cookies to collect sensitive personal information, and we do not link cookie data to any identifiable individual without your consent.',
    ],
  },
  {
    id: 'retention',
    title: 'Data Retention',
    icon: 'zmdi-time',
    content: [
      'We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, including for the duration of your account (if applicable) and for a reasonable period thereafter.',
      'Order records, including personal details and purchase history, are retained for a minimum of seven years to comply with financial, tax, and legal obligations.',
      'If you unsubscribe from marketing communications, we will retain your email address on a suppression list to ensure you are not accidentally re-subscribed.',
      'When your data is no longer required, we will securely delete or anonymise it in accordance with applicable data protection regulations.',
    ],
  },
  {
    id: 'security',
    title: 'Data Security',
    icon: 'zmdi-lock',
    content: [
      'We implement appropriate technical and organisational security measures to protect your personal information from unauthorised access, disclosure, alteration, and destruction.',
      'Our website uses SSL (Secure Sockets Layer) encryption for all data transmission. Look for the padlock icon in your browser address bar to confirm a secure connection.',
      'Access to your personal data within our organisation is restricted to personnel who have a legitimate need to access it as part of their role.',
      'While we take every reasonable precaution, no method of data transmission over the internet is 100% secure. We cannot guarantee absolute security, but we are committed to minimising risk.',
      'In the unlikely event of a data breach that poses a risk to your rights and freedoms, we will notify you and the relevant authorities as required by applicable law.',
    ],
  },
  {
    id: 'rights',
    title: 'Your Rights',
    icon: 'zmdi-account-circle',
    content: [
      'You have the right to access the personal data we hold about you, including your order history and any style or sizing preferences stored in your account. You may request a copy of your data at any time by contacting our support team.',
      'You have the right to request correction of any inaccurate or incomplete personal data we hold about you.',
      'In certain circumstances, you have the right to request the deletion of your personal data. Please note that some data may need to be retained for legal or contractual reasons.',
      'You have the right to object to the processing of your personal data for direct marketing purposes and to withdraw your consent for marketing communications at any time.',
      'You have the right to data portability — to receive your data in a structured, machine-readable format where technically feasible.',
      'If you believe we have not handled your data correctly, you have the right to lodge a complaint with the relevant data protection authority in your jurisdiction.',
    ],
  },
  {
    id: 'children',
    title: 'Children\'s Privacy',
    icon: 'zmdi-accounts',
    content: [
      'Our website and services are not directed at individuals under the age of 18. We do not knowingly collect personal data from minors.',
      'If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately and we will take steps to delete such data.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact & Complaints',
    icon: 'zmdi-email',
    content: [
      'If you have any questions, concerns, or requests relating to this Privacy Policy or the way we handle your personal data, please contact our dedicated privacy team through the contact form on our website.',
      'We aim to respond to all privacy-related enquiries within 5 business days.',
      'If you are not satisfied with our response, you have the right to contact your local data protection authority.',
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(PRIVACY_SECTIONS[0].id);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      for (const section of PRIVACY_SECTIONS) {
        const el = document.getElementById(`pp-section-${section.id}`);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(section.id);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`pp-section-${id}`);
    if (el) {
      const offset = 90;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Privacy Policy</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Hero ── */}
          <div className="pp-hero">
            <div className="pp-hero__icon">
              <i className="zmdi zmdi-shield-check"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Privacy Policy</h2>
            <p className="stext-113 cl6 pp-hero__sub">
              Your privacy matters to us. This policy explains exactly how we collect, use, and protect your personal information.
            </p>
            <p className="stext-113 cl6" style={{ fontSize: 12, marginTop: 6 }}>
              Last updated: June 2025 &nbsp;·&nbsp; {PRIVACY_SECTIONS.length} sections
            </p>
          </div>

          {/* ── Layout ── */}
          <div className="pp-layout">

            {/* Sticky sidebar nav */}
            <nav className="pp-nav">
              <p className="pp-nav__heading">Contents</p>
              {PRIVACY_SECTIONS.map((sec, i) => (
                <button
                  key={sec.id}
                  className={`pp-nav__item ${activeSection === sec.id ? 'pp-nav__item--active' : ''}`}
                  onClick={() => scrollTo(sec.id)}
                  type="button"
                >
                  <span className="pp-nav__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="pp-nav__label">{sec.title}</span>
                </button>
              ))}
            </nav>

            {/* Content */}
            <div className="pp-content">
              {PRIVACY_SECTIONS.map((sec, i) => (
                <div
                  key={sec.id}
                  id={`pp-section-${sec.id}`}
                  className="pp-section"
                >
                  <div className="pp-section__header">
                    <div className="pp-section__num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="pp-section__title-wrap">
                      <i className={`zmdi ${sec.icon} pp-section__icon`}></i>
                      <h4 className="pp-section__title">{sec.title}</h4>
                    </div>
                  </div>
                  <div className="pp-section__body">
                    {sec.content.map((para, j) => (
                      <p key={j} className="pp-section__para">{para}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer note */}
              <div className="pp-footnote">
                <i className="zmdi zmdi-lock" style={{ fontSize: 20, color: '#717fe0', marginBottom: 10, display: 'block' }}></i>
                <p className="stext-113 cl6">
                  We never sell your personal data to third parties. Your trust is as precious to us as the jewellery we craft.{' '}
                  <a href="/contact" className="pp-link">Get in touch</a> if you have any questions.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .pp-hero {
          text-align: center;
          padding-bottom: 56px;
        }
        .pp-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          border: 1px solid #e0e0f8;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 28px;
          color: #717fe0;
        }
        .pp-hero__sub {
          max-width: 540px;
          margin: 0 auto 8px;
          line-height: 1.7;
        }

        /* ── Layout ── */
        .pp-layout {
          display: grid;
          grid-template-columns: 230px 1fr;
          gap: 48px;
          align-items: start;
        }

        /* ── Sidebar nav ── */
        .pp-nav {
          position: sticky;
          top: 24px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .pp-nav__heading {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: #bbb;
          margin-bottom: 10px;
          padding-left: 12px;
        }
        .pp-nav__item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          border: none;
          border-radius: 5px;
          background: none;
          cursor: pointer;
          text-align: left;
          width: 100%;
          font-family: Poppins-Regular, sans-serif;
          transition: background 0.2s;
          border-left: 2px solid transparent;
        }
        .pp-nav__item:hover {
          background: #f5f5ff;
        }
        .pp-nav__item--active {
          background: #f0f2ff;
          border-left-color: #717fe0;
        }
        .pp-nav__num {
          font-size: 10px;
          font-weight: 700;
          color: #ccc;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
          transition: color 0.2s;
        }
        .pp-nav__item--active .pp-nav__num { color: #717fe0; }
        .pp-nav__label {
          font-size: 12px;
          color: #777;
          line-height: 1.4;
          transition: color 0.2s;
        }
        .pp-nav__item--active .pp-nav__label { color: #333; font-weight: 500; }

        /* ── Content sections ── */
        .pp-section {
          margin-bottom: 52px;
          scroll-margin-top: 90px;
        }
        .pp-section__header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e0e0f8;
        }
        .pp-section__num {
          font-size: 12px;
          font-weight: 700;
          color: #717fe0;
          letter-spacing: 0.5px;
          line-height: 1;
          margin-top: 3px;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
        }
        .pp-section__title-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .pp-section__icon {
          font-size: 18px;
          color: #717fe0;
        }
        .pp-section__title {
          font-size: 16px;
          font-weight: 600;
          color: #2c2c2c;
          margin: 0;
        }
        .pp-section__body {
          padding-left: 0;
        }
        .pp-section__para {
          font-size: 13.5px;
          color: #555;
          line-height: 1.85;
          margin: 0 0 14px;
          padding-left: 26px;
          position: relative;
        }
        .pp-section__para::before {
          content: '';
          position: absolute;
          left: 10px;
          top: 10px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #717fe0;
        }
        .pp-section__para:last-child { margin-bottom: 0; }

        /* ── Footnote ── */
        .pp-footnote {
          background: #f8f9ff;
          border: 1px solid #dde2f8;
          border-radius: 8px;
          padding: 28px 28px;
          text-align: center;
          margin-top: 16px;
        }
        .pp-link {
          color: #717fe0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: color 0.2s;
        }
        .pp-link:hover { color: #4a55c8; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .pp-layout {
            grid-template-columns: 1fr;
          }
          .pp-nav {
            position: static;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 4px;
          }
          .pp-nav__heading { display: none; }
          .pp-nav__item {
            flex: 0 0 auto;
            padding: 6px 12px;
            border-left: none;
            border-bottom: 2px solid transparent;
          }
          .pp-nav__item--active {
            border-left-color: transparent;
            border-bottom-color: #717fe0;
          }
          .pp-nav__num { display: none; }
          .pp-nav__label { font-size: 11px; }
        }
        @media (max-width: 480px) {
          .pp-section__para {
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;